<template>
  <v-dialog
    v-model="isCreateLmbModalOpen"
    :width="800"
  >
    <v-list :color="primaryColor">
      <v-list-item avatar>
        <v-list-item-avatar>
          <v-icon color="white">add_circle_outline</v-icon>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title>
            <span class="white--text">Create Learner Made Bulb</span>
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>
    <v-card
      :elevation="0"
      class="rounded-0"
    >
      <v-container>
        <v-list three-line>
          <v-list-item>
            <v-text-field
              autofocus
              v-model="lmbData.name"
              :error-messages="nameErrors"
              label="Name of Learner Made Bulb"
              @input="$v.lmbData.name.$touch"
              :disabled="isProcessingData"
            ></v-text-field>
          </v-list-item>
          <v-list-item>
            <v-textarea
              :rows="2"
              no-resize
              v-model="lmbData.learningObjective"
              label="Learning objective/instructions"
              counter="255"
              maxlength="255"
              :disabled="isProcessingData"
            ></v-textarea>
          </v-list-item>
        </v-list>
        <v-btn
          @click="isAdvancedSettingsOpen = !isAdvancedSettingsOpen"
          plain
        >
          Advanced settings
        </v-btn>
        <v-list
          v-if="isAdvancedSettingsOpen"
          dense
        >
          <v-list-item>
            <v-text-field
              v-model.number="lmbData.points"
              type="number"
              :max="100"
              :min="0"
              :error-messages="pointsErrors"
              label="Points"
              required
              :disabled="isProcessingData"
            ></v-text-field>
          </v-list-item>
          <v-list-item>
            <v-switch
              v-model="lmbData.allowPrivateVideoHosts"
              label="Allow learners to use private video hosts"
              hide-details
            ></v-switch>
          </v-list-item>
          <v-list-item
            v-if="schoolPermissionKeys.includes('enableAllowLmbStudentVideoIntegrationAccessToggle')"
            class="pl-9"
          >
            <v-switch
              v-model="lmbData.learnersAccessTheirOwnVideoIntegrationContent"
              :disabled="!lmbData.allowPrivateVideoHosts"
              hide-details
            >
              <template v-slot:label>
                <div id="lmb-private-video-host-content-label">
                  Learners will access their own content within private video hosts
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        color="primary"
                        class="nudge-tooltip ml-2"
                        v-on="on"
                        v-bind="attrs"
                      >
                        info_outline
                      </v-icon>
                    </template>
                    <span>If not enabled, learners will access their teacher's content within private video hosts.</span>
                  </v-tooltip>
                </div>
              </template>
            </v-switch>
          </v-list-item>
          <v-list-item v-if="(
            user.hasHosting
            && !user.hasLimitedVideoUploadAccess
          )">
            <v-switch
              v-model="lmbData.allowUpload"
              hide-details
            >
              <template v-slot:label>
                Allow learners to upload video files
                <div>
                  <v-btn
                    text
                    icon
                    :color="primaryColor"
                    href="https://knowledge.playposit.com/article/214-learner-made-bulbs-in-3-0#lmb-upload"
                    target="_blank"
                  >
                    <v-icon>info</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-switch>
          </v-list-item>
          <v-list-item>
            <v-switch
              v-model="lmbData.autograde"
              hide-details
            >
              <template v-slot:label>
                Enable complete/incomplete grading
              </template>
            </v-switch>
          </v-list-item>
        </v-list>
      </v-container>
      <v-card-actions class="d-flex justify-space-between">
        <v-btn
          text
          color="grey"
          @click="isCreateLmbModalOpen = false"
          :disabled="isProcessingData"
        >
          Cancel
        </v-btn>
        <v-btn
          :color="primaryColor"
          elevation="0"
          :disabled="isCreateLmbButtonDisabled"
          @click="createLmb"
          :loading="isProcessingData"
        >
          <span class="white--text">Save</span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  mapState,
  mapMutations,
} from 'vuex';
import { mapFields } from 'vuex-map-fields';
import {
  maxLength,
  required,
  between,
} from 'vuelidate/lib/validators';

export default {
  name: 'CreateLmbModal',
  data: () => ({
    isAdvancedSettingsOpen: false,
    isProcessingData: false,
    originalLmbData: {
      name: '',
      learningObjective: '',
      points: 10,
      allowUpload: false,
      autograde: false,
      allowPrivateVideoHosts: true,
      learnersAccessTheirOwnVideoIntegrationContent: false,
    },
    lmbData: {
      name: '',
      learningObjective: '',
      points: 10,
      allowUpload: false,
      autograde: false,
      allowPrivateVideoHosts: true,
      learnersAccessTheirOwnVideoIntegrationContent: false,
    },
  }),
  validations: {
    lmbData: {
      name: {
        required,
        maxLength: maxLength(255),
      },
      points: {
        required,
        between: between(0, 100),
      },
    },
  },
  computed: {
    ...mapState([
      'playlist',
      'user',
    ]),
    ...mapFields([
      'isCreateLmbModalOpen',
    ]),
    isCreateLmbButtonDisabled() {
      return (
        !this.lmbData.name?.length
        || this.nameErrors.length
        || this.pointsErrors.length
        || this.isProcessingData
      );
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.lmbData.name.$dirty) return errors;
      if (!this.$v.lmbData.name.required) errors.push('Name is required.');
      if (!this.$v.lmbData.name.maxLength) errors.push('Name can not exceed 255 characters.');
      return errors;
    },
    pointsErrors() {
      const errors = [];
      if (this.lmbData.points === '') errors.push('Integer points are required.');
      if (!this.$v.lmbData.points.required) errors.push('Points are required.');
      if (!this.$v.lmbData.points.between) errors.push('Points must be between 0 and 100.');
      return errors;
    },
    primaryColor() {
      return this.playlist.default_color ? this.playlist.default_color : '#2196F3';
    },
    schoolPermissionKeys() {
      return this.playlist?.school?.permissionKeys ?? [];
    },
  },
  methods: {
    ...mapMutations([
      'toggleSnackbar',
    ]),
    async createLmb() {
      try {
        this.isProcessingData = true;
        await this.$api.playlist.createLmb({
          name: this.lmbData.name,
          learningObjective: this.lmbData.learningObjective || 'Upload your video, add interactions, and submit it to your instructor.',
          points: this.lmbData.points,
          allowUpload: this.lmbData.allowUpload,
          autograde: this.lmbData.points === 0 ? true : this.lmbData.autograde,
          allowPrivateVideoHosts: this.lmbData.allowPrivateVideoHosts,
          learnersAccessTheirOwnVideoIntegrationContent: this.lmbData.learnersAccessTheirOwnVideoIntegrationContent,
        });
        this.toggleSnackbar({
          text: 'Learner Made Bulb successfully created! You can now add it to a module.',
          color: 'success',
        });
        this.isCreateLmbModalOpen = false;
      } catch(e) {
        this.toggleSnackbar({
          text: 'Unable to create a Learner Made Bulb at this time.',
          color: 'error',
        });
      } finally {
        this.isProcessingData = false;
      }
    },
  },
  watch: {
    isCreateLmbModalOpen(newValue) {
      if (newValue) {
        this.$v.$reset();
        this.lmbData = _.cloneDeep(this.originalLmbData);
      }
    },
  },
};
</script>

<style lang="scss">
.nudge-tooltip {
  position: relative;
  top: -1px;
}
</style>
